// What the Unassigned faces and Unassigned voices tabs share: one page of rows nobody is
// confirmed on, the ids the analyst ticked, and the two writes that move them into an identity
// — a new one, or an existing one. The rows and the endpoints are the caller's.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { detailMessage } from '@/features/humint/mappers/errors'

/** The naming parts a create sends; the server needs one of them filled. */
export interface ManualIdentityNames {
  first_name?: string
  last_name?: string
  nickname?: string
}

export interface UnassignedPageSource<Page, Identity> {
  queryKey: (page: number) => readonly unknown[]
  fetchPage: (page: number, pageSize: number) => Promise<Page>
  total: (page: Page) => number
  pageSize: number
  enabled: boolean
  create: (names: ManualIdentityNames, selected: string[]) => Promise<Identity>
  assign: (identityId: string, selected: string[]) => Promise<Identity>
  /** What a write leaves stale, invalidated after it. */
  invalidate: (queryClient: ReturnType<typeof useQueryClient>) => void
}

export interface UnassignedSelection<Page, Identity> {
  data: Page | undefined
  total: number
  page: number
  totalPages: number
  isLoading: boolean
  /** The ids the analyst ticked, in the order the rows are shown. */
  selected: string[]
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  clearSelection: () => void
  setPage: (page: number) => void
  /** A write is in flight — the dialogs disable their confirm button. */
  isSubmitting: boolean
  /**
   * The last write's refusal: the server's own sentence (a 409 names the other identity); an
   * unexplained failure is an empty string, and the tab translates its own.
   */
  error: string | null
  clearError: () => void
  createIdentity: (names: ManualIdentityNames) => Promise<Identity | null>
  assignToIdentity: (identityId: string) => Promise<Identity | null>
}

export function useUnassignedSelection<Page, Identity>(
  source: UnassignedPageSource<Page, Identity>
): UnassignedSelection<Page, Identity> {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: source.queryKey(page),
    queryFn: () => source.fetchPage(page, source.pageSize),
    enabled: source.enabled,
    placeholderData: keepPreviousData,
  })

  const afterWrite = useCallback(() => {
    setSelected([])
    source.invalidate(queryClient)
  }, [queryClient, source.invalidate])

  const create = useMutation({
    mutationFn: (names: ManualIdentityNames) => source.create(names, selected),
    onSuccess: afterWrite,
  })
  const assign = useMutation({
    mutationFn: (identityId: string) => source.assign(identityId, selected),
    onSuccess: afterWrite,
  })

  const toggle = useCallback((id: string) => {
    setSelected(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    )
  }, [])

  const runWrite = async <T>(work: () => Promise<T>): Promise<T | null> => {
    setError(null)
    try {
      return await work()
    } catch (caught) {
      setError(detailMessage(caught))
      return null
    }
  }

  const total = query.data ? source.total(query.data) : 0
  return {
    data: query.data,
    total,
    page,
    totalPages: Math.max(1, Math.ceil(total / source.pageSize)),
    isLoading: query.isLoading,
    selected,
    isSelected: id => selected.includes(id),
    toggle,
    clearSelection: () => setSelected([]),
    setPage,
    isSubmitting: create.isPending || assign.isPending,
    error,
    clearError: () => setError(null),
    createIdentity: names => runWrite(() => create.mutateAsync(names)),
    assignToIdentity: identityId => runWrite(() => assign.mutateAsync(identityId)),
  }
}
